import React, { useState, useEffect } from "react";
import VoucherTemplateForm from "../components/vouchers/VoucherTemplateForm";
import VoucherInstancesTab from "../components/vouchers/VoucherInstancesTab";
import {
  getVoucherTemplates,
  createVoucherTemplate,
  updateVoucherTemplate,
  deleteVoucherTemplate,
} from "../services/voucherTemplates.service";
import type {
  VoucherTemplate,
  CreateVoucherTemplateDTO,
} from "../types/voucherTemplate.types";

type TabType = "templates" | "redemptions" | "campaigns";

const VoucherTemplates: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>("templates");
  const [templates, setTemplates] = useState<VoucherTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] =
    useState<VoucherTemplate | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Load templates
  useEffect(() => {
    if (activeTab === "templates") {
      loadTemplates();
    }
  }, [activeTab]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await getVoucherTemplates();
      setTemplates(data);
    } catch (err) {
      console.error("Error loading templates:", err);
      setError("Failed to load voucher templates");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowForm(true);
    setError(null);
    setSuccess(null);
  };

  const handleEdit = (template: VoucherTemplate) => {
    setEditingTemplate(template);
    setShowForm(true);
    setError(null);
    setSuccess(null);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this voucher template?")) {
      return;
    }

    try {
      await deleteVoucherTemplate(id);
      setSuccess("Template deleted successfully");
      loadTemplates();
    } catch (err) {
      console.error("Error deleting template:", err);
      setError("Failed to delete template");
    }
  };

  const handleSubmit = async (data: CreateVoucherTemplateDTO) => {
    try {
      setSubmitting(true);
      setError(null);

      if (editingTemplate) {
        await updateVoucherTemplate(editingTemplate.id, data);
        setSuccess("Template updated successfully");
      } else {
        await createVoucherTemplate(data);
        setSuccess("Template created successfully");
      }

      setShowForm(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (err) {
      console.error("Error saving template:", err);
      setError("Failed to save template");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTemplate(null);
    setError(null);
  };

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Vouchers</h1>
        <p className="text-gray-600 mt-1">
          Manage voucher templates, view redemptions, and track campaigns
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab("templates")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "templates"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Templates
          </button>
          <button
            onClick={() => setActiveTab("redemptions")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "redemptions"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Redemptions
          </button>
          <button
            onClick={() => setActiveTab("campaigns")}
            className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === "campaigns"
                ? "border-primary-500 text-primary-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Campaigns
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "templates" && (
        <div>
          {/* Success/Error Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-center justify-between">
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-red-900 hover:text-red-700"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center justify-between">
              <span>{success}</span>
              <button
                onClick={() => setSuccess(null)}
                className="text-green-900 hover:text-green-700"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Create Button */}
          {!showForm && (
            <div className="mb-6 flex justify-end">
              <button
                onClick={handleCreate}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create Template
              </button>
            </div>
          )}

          {/* Form */}
          {showForm && (
            <div className="mb-6 bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingTemplate ? "Edit Template" : "Create New Template"}
              </h2>
              <VoucherTemplateForm
                template={editingTemplate}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                loading={submitting}
              />
            </div>
          )}

          {/* Templates List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
              <p className="text-gray-600 mt-4">Loading templates...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-12 text-center">
              <svg
                className="w-16 h-16 text-gray-300 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No voucher templates yet
              </h3>
              <p className="text-gray-600 mb-4">
                Create your first template to get started
              </p>
              <button
                onClick={handleCreate}
                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Create Template
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Promo Code
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Discount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organizations
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      States
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {templates.map((template) => (
                    <tr key={template.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {template.voucher_name}
                        </div>
                        {template.qr_code_url && (
                          <div className="text-xs text-gray-500 mt-1">
                            <a
                              href={template.qr_code_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-600 hover:text-primary-800"
                            >
                              View QR URL
                            </a>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm text-gray-900">
                          {template.voucher_promo_code}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-semibold text-green-600">
                          ${template.discount_value.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {template.organizations.length === 0 ? (
                            <span className="text-gray-400">All</span>
                          ) : (
                            <div className="space-y-1">
                              {template.organizations
                                .slice(0, 2)
                                .map((org, idx) => (
                                  <div key={idx} className="text-xs">
                                    {org.org_name}
                                    {org.location_name &&
                                      ` - ${org.location_name}`}
                                  </div>
                                ))}
                              {template.organizations.length > 2 && (
                                <div className="text-xs text-gray-500">
                                  +{template.organizations.length - 2} more
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {template.states.length === 0 ? (
                            <span className="text-gray-400">All</span>
                          ) : template.states.length <= 3 ? (
                            template.states.join(", ")
                          ) : (
                            `${template.states.slice(0, 3).join(", ")} +${template.states.length - 3}`
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleEdit(template)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(template.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === "redemptions" && <VoucherInstancesTab />}

      {activeTab === "campaigns" && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <svg
            className="w-16 h-16 text-gray-300 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Campaigns Coming Soon
          </h3>
          <p className="text-gray-600">
            Campaign management features will be available here
          </p>
        </div>
      )}
    </div>
  );
};

export default VoucherTemplates;
